/**
 * Was das Abzeichen eines Bots sagt (`L3a`, **E104** Teilfragen 3 und 5, **E112**) — in der Lobby
 * und im Spiel aus **derselben** Funktion, damit beide nicht auseinanderlaufen.
 *
 * - **Das Etikett** ist „Bot", dahinter die **Stufe**, wenn die Einstellung ihr entspricht (sie
 *   wird im Dienst berechnet, **E106**; nach einer Abweichung entfällt sie, E104 Teilfrage 5), und
 *   die **Strategie**. Solange `stark` offen ist, steht nur die Stufe (**E112**, 1).
 * - **ⓘ** zeigt die Einstellungen: bei einer Suche Tiefe und Gewichte, bei Greedy die Gewichte,
 *   beim Sprachmodell Anbieter und Modell — **nie einen Preis** (**E112**, 3). Zufall hat nichts
 *   einzustellen und bekommt **kein** ⓘ.
 *
 * **Gelesen wird nur die Positivliste** der Lobby-Ansicht (`lobbyBotSchema`, **E108**) — nie die
 * Definition, und nie mehr, als darin steht. Fehlt sie, bleibt es bei „Bot": **dass** ein Platz ein
 * Bot ist, kommt im Spiel aus dem Zustand (**E53**), und das Abzeichen verschwindet dann nicht.
 */

import type { LobbyBotDto } from '@flaghex/contracts';

import type { FallbackSummary } from '../game/history.js';

import { de } from '../i18n/index.js';

const SEPARATOR = ' · ';
const NBSP = '\u00a0';

export interface BotBadgeView {
  readonly label: string;
  /** Die Zeilen hinter ⓘ — leer heißt: kein ⓘ. */
  readonly info: readonly string[];
}

/** Der Ausschnitt von `t`, den diese Funktion braucht — damit sie ohne React prüfbar bleibt. */
export type Translate = (key: string, options: Record<string, string | number>) => string;

const number = new Intl.NumberFormat('de-DE');

export function botBadgeOf(
  bot: LobbyBotDto | undefined,
  t: Translate,
  fallbacks?: FallbackSummary | undefined,
): BotBadgeView {
  const view = describe(bot, t);
  // **Der Rückfall ändert das Etikett nicht** (E112, 2): die Definition ist dieselbe, nur
  // einzelne Züge kamen von `greedy`. ⓘ zählt sie — und hat damit auch ohne Beschreibung etwas.
  if (fallbacks === undefined || fallbacks.count === 0) return view;
  const count = t('lobby:bot.badge.fallbacks', { count: fallbacks.count });
  const line =
    fallbacks.lastReason === undefined
      ? count
      : `${count} ${t('lobby:bot.badge.fallbacksLast', {
          reason: t(`lobby:bot.badge.reasons.${fallbacks.lastReason}`, {}),
        })}`;
  return { label: view.label, info: [...view.info, line] };
}

function describe(bot: LobbyBotDto | undefined, t: Translate): BotBadgeView {
  const bare = t('lobby:slots.bot', {});
  if (bot === undefined) return { label: bare, info: [] };

  const level = bot.level === undefined ? [] : [t(`lobby:bot.levels.${bot.level}`, {})];
  if ('resolvesAtStart' in bot) {
    return {
      label: [bare, ...level].join(SEPARATOR),
      info: [t('lobby:bot.badge.resolvesAtStart', {})],
    };
  }

  const label = [bare, ...level, t(`lobby:bot.strategies.${bot.strategy}`, {})].join(SEPARATOR);

  if (bot.model !== undefined) {
    return {
      label,
      info: [
        t('lobby:bot.badge.provider', { id: bot.model.providerId }),
        t('lobby:bot.badge.model', { id: bot.model.modelId }),
      ],
    };
  }

  const weights = Object.entries(bot.weights ?? {}).map(
    // Ein Gewicht, das die Oberfläche (noch) nicht kennt, steht mit seiner Kennung da — es ist
    // trotzdem Teil der Definition. **Zwischen Name und Zahl ein geschütztes Leerzeichen**: in
    // der Sichtprüfung von `L3a` stand „Fähnrich nah am Ziel" am Zeilenende und die 8 darunter.
    ([name, value]) =>
      `${name in de.lobby.bot.weights ? t(`lobby:bot.weights.${name}`, {}) : name}${NBSP}${number.format(value)}`,
  );
  return {
    label,
    info: [
      ...(bot.depth === undefined ? [] : [t('lobby:bot.badge.depth', { depth: String(bot.depth) })]),
      ...(weights.length === 0 ? [] : [weights.join(SEPARATOR)]),
    ],
  };
}
